/**
 * CSV Utility Functions
 * Handles CSV parsing, validation, template generation, and export
 */

/**
 * Parse CSV string into array of objects
 * @param {string} csvText - Raw CSV text
 * @returns {Array} Array of objects with headers as keys
 */
export const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];

  const headers = lines[0].split(',').map(h => h.trim());
  const data = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const row = {};

    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    data.push(row);
  }

  return data;
};

/**
 * Generate CSV template for accounts
 * @returns {string} CSV template string
 */
export const generateAccountsTemplate = () => {
  const headers = [
    'Account Name',
    'Account Type',
    'Institution',
    'Contribution Amount',
    'Contribution Frequency'
  ];

  const examples = [
    'My 401k,401k,Vanguard,500,monthly',
    'Roth IRA,roth_ira,Fidelity,600,monthly',
    'Stocks & Shares ISA,stocks_shares_isa,Hargreaves Lansdown,1000,annual'
  ];

  return headers.join(',') + '\n' + examples.join('\n');
};

/**
 * Generate CSV template for investments
 * @returns {string} CSV template string
 */
export const generateInvestmentsTemplate = () => {
  const headers = [
    'Investment Name',
    'Ticker',
    'Amount',
    'Expected Return (%)',
    'Risk Level',
    'Category',
    'Account Name',
    'Purchase Date'
  ];

  const examples = [
    'S&P 500 Index Fund,SPY,25000,7,Medium,Stocks,My 401k,2023-01-15',
    'Total Bond Market,BND,15000,3,Low,Bonds,Roth IRA,2023-01-15',
    'Bitcoin,BTC,5000,15,High,Cryptocurrency,Crypto Wallet,2024-03-01'
  ];

  return headers.join(',') + '\n' + examples.join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - File name
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Validate account CSV data
 * @param {Array} data - Parsed CSV data
 * @param {Array} accountTypes - Available account types
 * @returns {Object} { valid: boolean, errors: Array, warnings: Array }
 */
export const validateAccountsCSV = (data, accountTypes) => {
  const errors = [];
  const warnings = [];
  const validAccountTypes = accountTypes.map(t => t.value);

  data.forEach((row, index) => {
    const rowNum = index + 2; // +2 because of header and 0-index

    // Required fields
    if (!row['Account Name'] || row['Account Name'].trim() === '') {
      errors.push(`Row ${rowNum}: Account Name is required`);
    }

    if (!row['Account Type'] || row['Account Type'].trim() === '') {
      errors.push(`Row ${rowNum}: Account Type is required`);
    } else if (!validAccountTypes.includes(row['Account Type'])) {
      errors.push(`Row ${rowNum}: Invalid Account Type "${row['Account Type']}". Must be one of: ${validAccountTypes.join(', ')}`);
    }

    // Optional but validated fields
    if (row['Contribution Amount'] && row['Contribution Amount'] !== '') {
      const amount = parseFloat(row['Contribution Amount']);
      if (isNaN(amount) || amount < 0) {
        errors.push(`Row ${rowNum}: Contribution Amount must be a positive number`);
      }
    }

    if (row['Contribution Frequency'] && row['Contribution Frequency'] !== '') {
      const validFrequencies = ['monthly', 'annual'];
      if (!validFrequencies.includes(row['Contribution Frequency'].toLowerCase())) {
        warnings.push(`Row ${rowNum}: Contribution Frequency should be "monthly" or "annual". Using "monthly" as default.`);
      }
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validate investments CSV data
 * @param {Array} data - Parsed CSV data
 * @param {Array} accounts - Existing accounts
 * @returns {Object} { valid: boolean, errors: Array, warnings: Array }
 */
export const validateInvestmentsCSV = (data, accounts) => {
  const errors = [];
  const warnings = [];
  const accountNames = accounts.map(a => a.name);
  const validCategories = ['Stocks', 'Bonds', 'Real Estate', 'Commodities', 'Cryptocurrency', 'Cash'];
  const validRiskLevels = ['Low', 'Medium', 'High'];

  data.forEach((row, index) => {
    const rowNum = index + 2;

    // Required fields
    if (!row['Investment Name'] || row['Investment Name'].trim() === '') {
      errors.push(`Row ${rowNum}: Investment Name is required`);
    }

    if (!row['Amount'] || row['Amount'].trim() === '') {
      errors.push(`Row ${rowNum}: Amount is required`);
    } else {
      const amount = parseFloat(row['Amount']);
      if (isNaN(amount) || amount <= 0) {
        errors.push(`Row ${rowNum}: Amount must be a positive number`);
      }
    }

    if (!row['Expected Return (%)'] || row['Expected Return (%)'].trim() === '') {
      errors.push(`Row ${rowNum}: Expected Return (%) is required`);
    } else {
      const returnRate = parseFloat(row['Expected Return (%)']);
      if (isNaN(returnRate)) {
        errors.push(`Row ${rowNum}: Expected Return (%) must be a number`);
      }
    }

    if (!row['Risk Level'] || row['Risk Level'].trim() === '') {
      warnings.push(`Row ${rowNum}: Risk Level not specified. Will default to "Medium"`);
    } else if (!validRiskLevels.includes(row['Risk Level'])) {
      errors.push(`Row ${rowNum}: Risk Level must be one of: ${validRiskLevels.join(', ')}`);
    }

    if (!row['Account Name'] || row['Account Name'].trim() === '') {
      warnings.push(`Row ${rowNum}: Account Name not specified. Will be marked as "Unassigned"`);
    } else if (accountNames.length > 0 && !accountNames.includes(row['Account Name'])) {
      warnings.push(`Row ${rowNum}: Account "${row['Account Name']}" not found. Investment will be marked as "Unassigned"`);
    }

    // Optional but validated fields
    if (row['Category'] && row['Category'] !== '') {
      if (!validCategories.includes(row['Category'])) {
        warnings.push(`Row ${rowNum}: Category "${row['Category']}" is not standard. Valid categories: ${validCategories.join(', ')}`);
      }
    } else {
      warnings.push(`Row ${rowNum}: No category specified. Will default to "Stocks"`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Convert account CSV data to account objects
 * @param {Array} data - Parsed CSV data
 * @param {Array} accountTypes - Available account types
 * @returns {Array} Array of account objects ready for Firebase
 */
export const convertCSVToAccounts = (data, accountTypes) => {
  return data.map(row => {
    const selectedType = accountTypes.find(t => t.value === row['Account Type']);
    const accountPurpose = selectedType?.type === 'retirement' || selectedType?.type === 'pension'
      ? 'retirement'
      : selectedType?.type === 'taxable'
      ? 'taxable'
      : selectedType?.type === 'crypto'
      ? 'crypto'
      : 'savings';

    return {
      name: row['Account Name'].trim(),
      type: row['Account Type'].trim(),
      institution: row['Institution']?.trim() || 'Not specified',
      accountPurpose,
      contributionAmount: row['Contribution Amount'] ? parseFloat(row['Contribution Amount']) : 0,
      contributionFrequency: row['Contribution Frequency']?.toLowerCase() || 'monthly',
      addedAt: new Date().toISOString()
    };
  });
};

/**
 * Convert investment CSV data to investment objects
 * @param {Array} data - Parsed CSV data
 * @param {Array} accounts - Existing accounts
 * @returns {Array} Array of investment objects ready for Firebase
 */
export const convertCSVToInvestments = (data, accounts) => {
  return data.map(row => {
    const account = accounts.find(a => a.name === row['Account Name']);

    return {
      name: row['Investment Name'].trim(),
      ticker: row['Ticker']?.trim() || '',
      amount: parseFloat(row['Amount']),
      return: parseFloat(row['Expected Return (%)']) || 7,
      risk: row['Risk Level']?.trim() || 'Medium',
      category: row['Category']?.trim() || 'Stocks',
      accountId: account?.id || null,
      accountName: account?.name || 'Unassigned',
      purchaseDate: row['Purchase Date']?.trim() || new Date().toISOString().split('T')[0]
    };
  });
};

/**
 * Export accounts to CSV
 * @param {Array} accounts - Array of account objects
 * @returns {string} CSV string
 */
export const exportAccountsToCSV = (accounts) => {
  const headers = [
    'Account Name',
    'Account Type',
    'Institution',
    'Contribution Amount',
    'Contribution Frequency'
  ];

  const rows = accounts.map(account => [
    account.name,
    account.type,
    account.institution || '',
    account.contributionAmount || '',
    account.contributionFrequency || 'monthly'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Export investments to CSV
 * @param {Array} investments - Array of investment objects
 * @param {Array} accounts - Array of account objects
 * @returns {string} CSV string
 */
export const exportInvestmentsToCSV = (investments, accounts) => {
  const headers = [
    'Investment Name',
    'Ticker',
    'Amount',
    'Expected Return (%)',
    'Risk Level',
    'Category',
    'Account Name',
    'Purchase Date'
  ];

  const rows = investments.map(investment => {
    const account = accounts.find(a => a.id === investment.accountId);

    return [
      investment.name,
      investment.ticker || '',
      investment.amount,
      investment.return || 7,
      investment.risk || 'Medium',
      investment.category || 'Stocks',
      account?.name || investment.accountName || 'Unassigned',
      investment.purchaseDate || ''
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Read file as text
 * @param {File} file - File object
 * @returns {Promise<string>} File content as text
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

export default {
  parseCSV,
  generateAccountsTemplate,
  generateInvestmentsTemplate,
  downloadCSV,
  validateAccountsCSV,
  validateInvestmentsCSV,
  convertCSVToAccounts,
  convertCSVToInvestments,
  exportAccountsToCSV,
  exportInvestmentsToCSV,
  readFileAsText
};
